import { matchPath } from "react-router-dom";
import { check_tab_access } from "../../helpers/AccessHelper";
import template_consumer from '../../assets/xlsx/Loanbook Template - Consumer.xlsx';
import template_sme from '../../assets/xlsx/Loanbook Template - SME.xlsx';
import template_ecs from '../../assets/xlsx/Portfolio Template - ECS.xlsx';
import template_cash_flows from '../../assets/xlsx/Cash Flows Template.xlsx';
import template_opportunities from '../../assets/xlsx/Opportunities Template.xlsx';
import info_consumer from '../../assets/pdf/Information Sheet - Consumer.pdf';
import info_sme from '../../assets/pdf/Information Sheet - SME.pdf';
import info_ecs from '../../assets/pdf/Information Sheet - ECS.pdf';
import info_cash_flows from '../../assets/pdf/Information Sheet - Cash Flows.pdf';
import info_opportunities from '../../assets/pdf/Information Sheet - Opportunities.pdf';
import user_guide_sme from '../../assets/pdf/User Guide - SME.pdf';
import user_guide_consumer from '../../assets/pdf/User Guide - Consumer.pdf';
import user_guide_upload from '../../assets/pdf/User Guide - File Upload.pdf';

// Shared by Sidebar.js (the nav itself) and NavigationBar.js (the topbar's
// breadcrumb), so the two can never disagree about what a page is called or
// which section it lives in.

// Cosmetic sidebar sections, rendered in this order. Distinct from each item's
// `group` field, which is access control (see check_tab_access) and must not be reused here.
export const SECTION_ORDER = ["Data", "Reporting", "Admin"];

// Unlike the File Upload page's version of this menu, the sidebar isn't scoped to a
// selected loan data type, so every info sheet/template/guide is listed unfiltered.
export const GUIDE_MENU_ITEMS = [
    {
        label: "Information Sheets",
        icon: "pi pi-info-circle",
        items: [
            { label: "Information Sheet - SME", url: info_sme, target: "_blank" },
            { label: "Information Sheet - Consumer", url: info_consumer, target: "_blank" },
            { label: "Information Sheet - ECS", url: info_ecs, target: "_blank" },
            { label: "Information Sheet - Cash Flows", url: info_cash_flows, target: "_blank" },
            { label: "Information Sheet - Opportunities", url: info_opportunities, target: "_blank" },
        ],
    },
    {
        label: "Templates",
        icon: "pi pi-file-excel",
        items: [
            { label: "Loanbook Template - SME", url: template_sme, target: "_blank" },
            { label: "Loanbook Template - Consumer", url: template_consumer, target: "_blank" },
            { label: "Portfolio Template - ECS", url: template_ecs, target: "_blank" },
            { label: "Cash Flows Template", url: template_cash_flows, target: "_blank" },
            { label: "Opportunities Template", url: template_opportunities, target: "_blank" },
        ],
    },
    {
        label: "User Guides",
        icon: "pi pi-file-pdf",
        items: [
            { label: "User Guide - SME", url: user_guide_sme, target: "_blank" },
            { label: "User Guide - Consumer", url: user_guide_consumer, target: "_blank" },
            { label: "User Guide - File Upload", url: user_guide_upload, target: "_blank" },
        ],
    },
];

// Same items, groups and gates as the sidebar has always had - only the
// PNG icons were swapped for PrimeIcons (`icon`) so they tint with the theme.
// `features` (useFeatures) hides env-switched pages this deployment has off.
export const get_nav_items = (groups, platform, is_viewing_as, features) => {
    const is_lend = Boolean(platform) && platform.toLowerCase() === "lend";
    const is_exaloan_account = !platform || platform.toLowerCase() === "exaloan";
    const nav_menu_items_ = [
        { id: 1, link_to: "/file-upload", icon: "pi-cloud-upload", text: "File Upload", section: "Data" },
        { id: 2, link_to: "/filetracking", icon: "pi-history", text: "File Tracking", section: "Data" },
        // Platform Analytics (Power BI) is superseded by Platform Report -
        // only Lend's own accounts keep the link. The route itself is
        // unchanged and still reachable by URL.
        ...(is_lend
            ? [{ id: 3, group: "Analytics", link_to: "/analytics", icon: "pi-chart-bar", text: "Originator Analytics", section: "Reporting", subsection: "Risk Analytics Report" }]
            : []),
        // ESG Toolbox, ABS Calculator, and Monitoring hidden from the
        // sidebar for now (temporary UI-only trim, not a feature
        // removal) - the routes/pages/backend are all still fully
        // intact and reachable by direct URL, just not linked from
        // nav. Uncomment to bring any of these back.
        // { id: 4, group: "ECS", link_to: "/climate-toolbox", icon: "pi-globe", text: "ESG Toolbox", section: "Reporting" },
        // { id: 9, group: "ABS", link_to: "/abs", icon: "pi-calculator", text: "ABS Calculator", section: "Reporting" },
        // { id: 5, group: "Admin", link_to: "/admin", icon: "pi-server", text: "Monitoring", section: "Admin" },
        // Exaloan staff only (cross-platform), even if a platform account
        // were ever put in the Monitoring group.
        ...(is_exaloan_account
            ? [{ id: 17, group: "Monitoring", link_to: "/activity", icon: "pi-history", text: "Originator Activity", section: "Data" }]
            : []),
        { id: 10, group: "Monitoring", link_to: "/cash-balance", icon: "pi-wallet", text: "Cash Balance", section: "Reporting", subsection: "Compartment Report" },
        { id: 11, group: "Monitoring", link_to: "/loan_check", icon: "pi-check-square", text: "Loan Check", section: "Reporting", subsection: "Compartment Report" },
        { id: 14, group: "Monitoring", link_to: "/compartment-report", icon: "pi-briefcase", text: "Compartment Report", section: "Reporting", subsection: "Compartment Report" },
        // No `section` - renders on its own at the top of the nav rather
        // than inside a collapsible section. No `group` (defaults to "User")
        // - open to every authenticated account, since GET /overview itself
        // already scopes an external account's token down to its own row.
        { id: 12, link_to: "/", icon: "pi-th-large", text: "Dashboard" },
        { id: 6, group: "Admin", link_to: "/platforms", icon: "pi-building", text: "Originators", section: "Admin" },
        // Every account, staff and platform alike - the backend scopes each
        // request to the account's own platform, and a platform that isn't
        // onboarded yet gets a "subscribe" message on the page itself
        // (PlatformReport.js). check_tab_access(groups, "Analytics") below
        // still applies as-is.
        { id: 13, group: "Analytics", link_to: "/platform-report", icon: "pi-chart-line", text: "Originator Report", section: "Reporting", subsection: "Risk Analytics Report" },
    ];
    const switched_off = (link) => (link === "/platform-report" && !features?.platform_report)
        || (link === "/compartment-report" && !features?.compartment_report);
    return nav_menu_items_.filter(obj => check_tab_access(groups, obj.group || "User") && !switched_off(obj.link_to));
};

// Breadcrumb trail for the topbar. Covers every route in App.js, including
// the ones deliberately not linked from the sidebar (reachable by URL only).
const ROUTE_TRAILS = [
    { path: "/", trail: () => ["Dashboard"] },
    { path: "/file-upload", trail: () => ["Data", "File Upload"] },
    { path: "/filetracking", trail: () => ["Data", "File Tracking"] },
    { path: "/analytics", trail: () => ["Reporting", "Originator Analytics"] },
    { path: "/platform-report", trail: () => ["Reporting", "Originator Report"] },
    { path: "/cash-balance", trail: () => ["Reporting", "Compartment Report", "Cash Balance"] },
    { path: "/loan_check", trail: () => ["Reporting", "Compartment Report", "Loan Check"] },
    { path: "/compartment-report", trail: () => ["Reporting", "Compartment Report"] },
    { path: "/climate-toolbox", trail: () => ["Reporting", "ESG Toolbox"] },
    { path: "/abs", trail: () => ["Reporting", "ABS Calculator"] },
    { path: "/admin", trail: () => ["Admin", "Monitoring"] },
    { path: "/platforms", trail: () => ["Admin", "Originators"] },
    { path: "/platforms/:platformName/api-access", trail: (p) => ["Admin", "Originators", p.platformName, "API Access"] },
    { path: "/activity", trail: () => ["Data", "Originator Activity"] },
    { path: "/activity/:platformKey", trail: (p) => ["Data", "Originator Activity", p.platformKey] },
    { path: "/platforms/:platformName", trail: (p) => ["Admin", "Originators", p.platformName] },
    { path: "/platforms/:platformName/users", trail: (p) => ["Admin", "Originators", p.platformName, "Users"] },
    { path: "/dashboard/:platformName", trail: (p) => ["Dashboard", p.platformName] },
    { path: "/logout", trail: () => ["Sign out"] },
];

export const get_route_trail = (pathname) => {
    for (const route of ROUTE_TRAILS) {
        const match = matchPath({ path: route.path, end: true }, pathname);
        if (match) {
            return route.trail(match.params);
        }
    }
    return [];
};
